import React from 'react';
import {v4 as uuidv4} from 'uuid';

import {ActionOrigin, StudyPhase, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  composeStudiesProceedToExecutionEventMetadata,
  STUDIES_PROCEED_TO_EXECUTION_ENDED_EVENT,
  STUDIES_PROCEED_TO_EXECUTION_STARTED_EVENT,
} from 'performance/processStudiesEvents';
import SetStudyPhaseDropdown, {
  SetStudyPhaseDropdownProps,
} from 'studies/workspace/overview/SetStudyPhaseDropdown';
import {ButtonSize} from 'ui-library/buttons/Button';

const PROCEED_TO_EXECUTION_MESSAGE = (
  <>
    <p>
      Proceeding will lock your treatment groups, sample definitions, and sample testing plans. Returning the
      study to the design phase will allow you to add new information, but not edit existing content.
    </p>
    <p>Review and finalize your updates before continuing— this action is irreversible.</p>
  </>
);

export interface SetClinicalStudyPhaseDropdownProps extends SetStudyPhaseDropdownProps {
  actionOrigin: ActionOrigin; // required for perf testing
}

function logProceedToExecutionTiming(
  studyId: string,
  studyType: StudyType
): (actionOrigin: ActionOrigin) => void {
  const id = `studies-proceed-to-execution-${studyId}-${uuidv4()}`;
  logProfilerTimingEvent({...STUDIES_PROCEED_TO_EXECUTION_STARTED_EVENT, id});
  return (actionOrigin: ActionOrigin) => {
    logProfilerTimingEvent(
      {...STUDIES_PROCEED_TO_EXECUTION_ENDED_EVENT, id},
      composeStudiesProceedToExecutionEventMetadata(studyType, actionOrigin)
    );
  };
}

export default function SetClinicalStudyPhaseDropdown({
  study,
  refetch,
  isDisabled,
  requiresApproval,
  allowReturnToDesignPhase,
  buttonSize,
  confirmPhaseChange,
  actionOrigin,
}: SetClinicalStudyPhaseDropdownProps): JSX.Element {
  return (
    <SetStudyPhaseDropdown
      study={study}
      refetch={refetch}
      isDisabled={isDisabled}
      requiresApproval={requiresApproval}
      buttonSize={buttonSize ?? ButtonSize.SMALL}
      allowReturnToDesignPhase={allowReturnToDesignPhase}
      confirmPhaseChange={
        confirmPhaseChange ?? {
          [StudyPhase.DESIGN]: null,
          [StudyPhase.EXECUTION]: {forwardsMessage: PROCEED_TO_EXECUTION_MESSAGE},
          [StudyPhase.COMPLETE]: null,
        }
      }
      actionOrigin={actionOrigin}
      proceedToExecutionLogger={logProceedToExecutionTiming}
    />
  );
}
